import { useState, useEffect, useRef } from 'react'
import { DashboardConfig, DashboardFilters } from '../types/dashboard'
import './SearchBar.css'

interface SearchBarProps {
  onFiltersChange: (filters: DashboardFilters) => void
  categories: string[]
  tags: string[]
  suggestions: DashboardConfig[]
  placeholder?: string
  className?: string
}

const SearchBar = ({
  onFiltersChange,
  categories,
  tags,
  suggestions,
  placeholder = "Search dashboards...",
  className = ''
}: SearchBarProps) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [selectedStatus, setSelectedStatus] = useState<DashboardFilters['status'] | ''>('')
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)
  const [focusedSuggestionIndex, setFocusedSuggestionIndex] = useState(-1)
  
  const searchInputRef = useRef<HTMLInputElement>(null)
  const suggestionsRef = useRef<HTMLDivElement>(null)

  // Update filters when any filter changes
  useEffect(() => {
    const filters: DashboardFilters = {
      search: searchTerm.trim() || undefined,
      category: selectedCategory || undefined,
      status: selectedStatus || undefined,
      tags: selectedTags.length > 0 ? selectedTags : undefined
    }
    
    onFiltersChange(filters)
  }, [searchTerm, selectedCategory, selectedTags, selectedStatus, onFiltersChange])

  // Handle search input changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)
    setShowSuggestions(value.length > 0)
    setFocusedSuggestionIndex(-1)
  }

  // Handle suggestion click
  const handleSuggestionClick = (dashboard: DashboardConfig) => {
    setSearchTerm(dashboard.name)
    setShowSuggestions(false)
    setFocusedSuggestionIndex(-1)
    searchInputRef.current?.focus()
  }

  // Handle category selection
  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCategory(e.target.value)
  }

  // Handle status selection
  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as DashboardFilters['status'] | ''
    setSelectedStatus(value)
  }

  // Handle tag toggle
  const handleTagToggle = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    )
  }

  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('')
    setSelectedCategory('')
    setSelectedTags([])
    setSelectedStatus('')
    setShowSuggestions(false)
    setFocusedSuggestionIndex(-1)
    searchInputRef.current?.focus()
  }

  // Handle keyboard navigation in suggestions
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setFocusedSuggestionIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setFocusedSuggestionIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        )
        break
      case 'Enter':
        e.preventDefault()
        if (focusedSuggestionIndex >= 0 && suggestions[focusedSuggestionIndex]) {
          handleSuggestionClick(suggestions[focusedSuggestionIndex])
        }
        break
      case 'Escape':
        setShowSuggestions(false)
        setFocusedSuggestionIndex(-1)
        break
    }
  }

  // Check if any filters are active
  const hasActiveFilters = searchTerm || selectedCategory || selectedTags.length > 0 || selectedStatus

  return (
    <div className={`search-bar ${className}`}>
      <div className="search-container">
        {/* Main Search Input */}
        <div className="search-input-container">
          <div className="search-input-wrapper">
            <span className="search-icon">🔍</span>
            <input
              ref={searchInputRef}
              type="text"
              className="search-input"
              placeholder={placeholder}
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              onFocus={() => setShowSuggestions(searchTerm.length > 0)}
              onBlur={() => {
                // Delay hiding suggestions to allow for clicks
                setTimeout(() => setShowSuggestions(false), 150)
              }}
            />
            {searchTerm && (
              <button
                className="clear-search-btn"
                onClick={() => setSearchTerm('')}
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>

          {/* Search Suggestions */}
          {showSuggestions && suggestions.length > 0 && (
            <div ref={suggestionsRef} className="suggestions-dropdown">
              {suggestions.map((dashboard, index) => (
                <div
                  key={dashboard.id}
                  className={`suggestion-item ${index === focusedSuggestionIndex ? 'focused' : ''}`}
                  onClick={() => handleSuggestionClick(dashboard)}
                >
                  <div className="suggestion-content">
                    <span className="suggestion-name">{dashboard.name}</span>
                    <span className="suggestion-description">{dashboard.description}</span>
                  </div>
                  <div className="suggestion-meta">
                    {dashboard.category && (
                      <span className="suggestion-category">{dashboard.category}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Filter Controls */}
        <div className="filter-controls">
          <button
            className={`advanced-toggle ${showAdvancedFilters ? 'active' : ''}`}
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            title="Toggle advanced filters"
          >
            <span className="toggle-icon">⚙️</span>
            <span className="toggle-text">Filters</span>
            {hasActiveFilters && <span className="active-indicator">●</span>}
          </button>

          {hasActiveFilters && (
            <button
              className="clear-filters-btn"
              onClick={handleClearFilters}
              title="Clear all filters"
            >
              Clear All
            </button>
          )}
        </div>
      </div>

      {/* Advanced Filters */}
      {showAdvancedFilters && (
        <div className="advanced-filters">
          <div className="filter-row">
            {/* Category Filter */}
            {categories.length > 0 && (
              <div className="filter-group">
                <label htmlFor="category-select" className="filter-label">Category</label>
                <select
                  id="category-select"
                  className="filter-select"
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                >
                  <option value="">All Categories</option>
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Status Filter */}
            <div className="filter-group">
              <label htmlFor="status-select" className="filter-label">Status</label>
              <select
                id="status-select"
                className="filter-select"
                value={selectedStatus}
                onChange={handleStatusChange}
              >
                <option value="">All Status</option>
                <option value="online">Online</option>
                <option value="offline">Offline</option>
                <option value="maintenance">Maintenance</option>
                <option value="development">Development</option>
              </select>
            </div>
          </div>

          {/* Tags Filter */}
          {tags.length > 0 && (
            <div className="filter-group tags-filter">
              <label className="filter-label">Tags</label>
              <div className="tags-container">
                {tags.map(tag => (
                  <button
                    key={tag}
                    className={`tag-filter ${selectedTags.includes(tag) ? 'selected' : ''}`}
                    onClick={() => handleTagToggle(tag)}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="active-filters">
          {selectedCategory && (
            <span className="active-filter">
              Category: {selectedCategory}
              <button onClick={() => setSelectedCategory('')}>✕</button>
            </span>
          )}
          {selectedStatus && (
            <span className="active-filter">
              Status: {selectedStatus}
              <button onClick={() => setSelectedStatus('')}>✕</button>
            </span>
          )}
          {selectedTags.map(tag => (
            <span key={tag} className="active-filter">
              Tag: {tag}
              <button onClick={() => handleTagToggle(tag)}>✕</button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

export default SearchBar 